import * as React from 'react'
import { connect, Dispatch } from 'react-redux'
import { Field, reduxForm } from 'redux-form'
import { compose } from 'redux'
import { asyncValidate, shouldAsyncValidate } from 'redux-form-yup'

import { TextInput, FieldConfig } from '../Inputs'
import {
  Form as StyledForm,
  PrimaryTextButton,
  FormSection
} from '../../../Styled/Shared'
import ToggleView from '../ToggleView'

import { SpaceType } from '../../../SpaceTypeSelect/spaceTypeQueries'
import { Store } from '../../../../reducers'
import {
  getResolvedLoadCase,
  EditableLoadCase,
  EditableLoad,
  getActiveCase
} from '../../../../reducers/project'
import { LoadCaseSchema } from '../validationSchema'
import {
  updateLoadCase,
  addLoad,
  selectLoad,
  deleteLoad
} from '../../../../actions/project'

import { AreaLoadForm, PointLoadForm, LineLoadForm } from './LoadTypeEditors'
import Delete from 'material-ui/svg-icons/action/delete'
import { handleKeyDown } from './shared'
import themeObj from '../../../../theme/defaultTheme'
import CustomSelect, { SelectProps } from '../../../Shared/CustomSelect'
import { recordEvent } from '../../../../analytics'

type ExternalProps = {
  spaceTypes: SpaceType[]
  onSubmit: any // tslint:disable-line
  topConcrete: number
}

/* We give each load a different form name to make sure we don't
   have to deal with updating the values.

   Additionally, we key the form component with the load case ID
   so that changing items (load cases) is guaranteed to recreate
   the form.
*/
// tslint:disable-next-line
function LoadEditor(props: any) {
  if (props.type === 'area') {
    return (
      <AreaLoadForm
        auto={props.auto}
        form={`area_${props.index}`}
        loadIndex={props.index}
        loadCaseId={props.loadCase}
        key={props.loadCase}
        initialValues={{
          name: props.load.name,
          value: props.load.values[0][1],
          type: props.load.type
        }}
      />
    )
  } else if (props.type === 'point') {
    return (
      <PointLoadForm
        form={`point_${props.index}`}
        auto={props.auto}
        loadIndex={props.index}
        loadCaseId={props.loadCase}
        key={props.loadCase}
        maxLength={props.beam ? props.beam.length : undefined}
        initialValues={{
          name: props.load.name,
          value: props.load.values[0][1],
          type: props.load.type,
          location: props.load.values[0][0]
        }}
      />
    )
  } else if (props.type === 'line') {
    let value2
    let location2

    if (props.load.values.length < 2) {
      value2 = 0
      location2 = props.beam.length
    } else {
      value2 = props.load.values[1][1]
      location2 = props.load.values[1][0]
    }
    return (
      <LineLoadForm
        form={`line_${props.index}`}
        auto={props.auto}
        autoValue={props.load._auto}
        loadIndex={props.index}
        loadCaseId={props.loadCase}
        key={props.loadCase}
        maxLength={props.beam ? props.beam.length : undefined}
        initialValues={{
          name: props.load.name,
          type: props.load.type,
          value: props.load.values[0][1],
          location: props.load.values[0][0],
          value2,
          location2
        }}
      />
    )
  } else {
    return null
  }
}

const parseOptions = options => {
  const pOptions = options.map(st => (
    <option value={st.id} key={st.id}>
      {st.displayName}
    </option>
  ))
  pOptions.unshift(
    <option key={pOptions.length} value="Temporary load">
      Temporary load
    </option>
  )
  pOptions.unshift(
    <option key={pOptions.length} value="Permanent load">
      Permanent load
    </option>
  )
  return pOptions
}

/* dbs-loads-load-name is used for input changes */

const addClick = (add, id) => e => {
  add(id)
  recordEvent('workspace', 'load-added')
  e.preventDefault()
}

const LoadsForm = props => {

  const concreteLoad = props.topConcrete * 25 / 1000
  const concreteLoads: EditableLoad[] = []
  concreteLoads.push({
    name: 'Top concrete (Left)',
    type: 'area',
    actsOn: [],
    autoId: 'slab.left.topconcrete',
    _auto: true,
    values: [[0, concreteLoad]],
  })
  concreteLoads.push({
    name: 'Top concrete (Right)',
    type: 'area',
    actsOn: [],
    autoId: 'slab.right.topconcrete',
    _auto: true,
    values: [[0, concreteLoad]],
  })
  console.log(props)
  return (
    <StyledForm
      onKeyDown={handleKeyDown(props.handleSubmit)}
      onSubmit={props.handleSubmit}
    >
      <h4>Load configuration</h4>
      <ToggleView title="Load case">
        <Field
          name="name"
          component={TextInput}
          className="dbs-loads-load-name"
          type="text"
          disabled={props.auto}
          autoFocus={true}
          props={{ title: 'Name' } as FieldConfig}
        />

        <label htmlFor="action">
          Action
          <Field
            name="action"
            component={CustomSelect}
            props={
              {
                options: parseOptions(props.spaceTypes),
                currentVal: props.initialValues.action
              } as SelectProps
            }
            disabled={true}
          />
        </label>
      </ToggleView>
      <hr />

      {props.loads.map((load, i) => {
        const extra =
          props.loads.length === 1 || props.auto ? null : (
            <button
              onClick={() => {
                recordEvent('workspace', 'load-deleted')
                props.delete(props.loadCaseId, i)
              }}
            >
              <Delete color={themeObj.colors.light} />
            </button>
          )
        return (
          <ToggleView
            title={load.name}
            key={i}
            open={props.activeLoad === i}
            onClick={() => props.select(props.loadCaseId, i)}
            extra={extra}
            secondary={true}
          >
            <FormSection>
              <LoadEditor
                type={load.type}
                index={i}
                load={load}
                loadCase={props.loadCaseId}
                auto={props.auto}
                beam={props.selectedBeam}
              />
            </FormSection>
          </ToggleView>
        )
      })}
      
      {concreteLoads.map((load, i) => {
        return (
          props.initialValues.name === 'Self weight (automatic)' &&
          props.initialValues.stage === 'finalComposite' &&
          props.topConcrete > 0 &&
          <ToggleView
            title={load.name}
            key={i + props.loads.length}
            open={props.activeLoad === i + props.loads.length}
            onClick={() => props.select(props.loadCaseId, i + props.loads.length)}
            secondary={true}
          >
            <FormSection>
              <LoadEditor 
                type={load.type}
                index={i + props.loads.length}
                load={load}
                loadCase={props.loadCaseId}
                auto={props.auto}
                beam={props.selectedBeam}
              />
            </FormSection>
          </ToggleView>
        )
      })}

      {!props.auto && (
        <PrimaryTextButton onClick={addClick(props.addLoad, props.loadCaseId)}>
          + Add load
        </PrimaryTextButton>
      )}
    </StyledForm>
  )
}

const mapStateToProps = (state: Store.Complete) => {
  const loadCase = getResolvedLoadCase(state)
  const activeCase = getActiveCase(state)

  if (loadCase == null) {
    throw new Error('Load editor without active load')
  }

  if (!activeCase) {
    throw new Error('Load editor without active case')
  }

  return {
    initialValues: loadCase,
    auto: loadCase.auto,
    loadCaseId: loadCase.id,
    loads: loadCase.loads,
    activeLoad: loadCase.activeLoad || 0,
    // TODO: (future) when we handle multiple beams, this needs a beam selector
    selectedBeam: activeCase.beams[0]
  }
}

const mapDispatchToProps = (dispatch: Dispatch<Store.Complete>) => ({
  addLoad: (loadCaseId: string) => dispatch(addLoad(loadCaseId)),
  select: (loadCaseId: string, idx: number) =>
    dispatch(selectLoad(loadCaseId, idx)),
  delete: (loadCaseId: string, idx: number) =>
    dispatch(deleteLoad(loadCaseId, idx))
})

// TODO: type this properly
export default compose(
  connect<{}, {}, ExternalProps>(mapStateToProps, mapDispatchToProps),
  reduxForm<Readonly<EditableLoadCase>>({
    form: 'loads',
    asyncValidate: asyncValidate(LoadCaseSchema),
    shouldAsyncValidate,
    onChange(values: EditableLoadCase, dispatch: Dispatch<Store.Complete>) {
      dispatch(updateLoadCase(values))
    }
  })
)(LoadsForm)



// WEBPACK FOOTER //
// ./src/Components/Workspace/Calculation/Editors/LoadEditor.tsx